import {
  DEFAULT_DATE_RANGE_PRESET,
  getDateRangeForPreset,
  type DateRangePreset,
} from '../../utils/dateRangePresets';

export type TypeFilter = 'all' | 'receipt' | 'sale' | 'adjustments' | 'starting_value' | 'oversell';

export type InventoryTransactionFilters = {
  typeFilter: TypeFilter;
  itemFilter: string;
  page: number;
  datePreset: DateRangePreset;
  startDate: string;
  endDate: string;
  searchQuery: string;
};

const defaultDateRange = getDateRangeForPreset(DEFAULT_DATE_RANGE_PRESET);

export const DEFAULT_INVENTORY_TRANSACTION_FILTERS: InventoryTransactionFilters = {
  typeFilter: 'all',
  itemFilter: '',
  page: 1,
  datePreset: DEFAULT_DATE_RANGE_PRESET,
  startDate: defaultDateRange.startDate,
  endDate: defaultDateRange.endDate,
  searchQuery: '',
};

const TYPE_VALUES: TypeFilter[] = [
  'all',
  'receipt',
  'sale',
  'adjustments',
  'starting_value',
  'oversell',
];

const DATE_PRESETS: DateRangePreset[] = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'last_30_days',
  'this_quarter',
  'last_quarter',
  'last_3_months',
  'last_6_months',
  'last_12_months',
  'this_year',
  'last_year',
  'all',
  'custom',
];

const parseEnum = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const parsePositiveInt = (value: string | null, fallback: number) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const parseInventoryTransactionFilters = (
  params: URLSearchParams
): InventoryTransactionFilters => {
  const datePreset = parseEnum(params.get('preset'), DATE_PRESETS, DEFAULT_DATE_RANGE_PRESET);

  let startDate = defaultDateRange.startDate;
  let endDate = defaultDateRange.endDate;

  if (datePreset === 'custom') {
    startDate = params.get('from') ?? '';
    endDate = params.get('to') ?? '';
  } else {
    const range = getDateRangeForPreset(datePreset);
    startDate = range.startDate;
    endDate = range.endDate;
  }

  const itemParam = params.get('item')?.trim() ?? '';
  const itemFilter =
    itemParam && Number.isFinite(Number(itemParam)) && Number(itemParam) > 0 ? itemParam : '';

  return {
    typeFilter: parseEnum(params.get('type'), TYPE_VALUES, 'all'),
    itemFilter,
    page: parsePositiveInt(params.get('page'), 1),
    datePreset,
    startDate,
    endDate,
    searchQuery: params.get('q')?.trim() ?? '',
  };
};

const isDefault = <K extends keyof InventoryTransactionFilters>(
  key: K,
  value: InventoryTransactionFilters[K]
) => value === DEFAULT_INVENTORY_TRANSACTION_FILTERS[key];

export const buildInventoryTransactionSearchParams = (
  filters: InventoryTransactionFilters
): URLSearchParams => {
  const params = new URLSearchParams();

  if (!isDefault('typeFilter', filters.typeFilter)) {
    params.set('type', filters.typeFilter);
  }
  if (filters.itemFilter) {
    params.set('item', filters.itemFilter);
  }
  if (!isDefault('page', filters.page)) {
    params.set('page', String(filters.page));
  }
  if (!isDefault('datePreset', filters.datePreset)) {
    params.set('preset', filters.datePreset);
  }
  if (filters.datePreset === 'custom') {
    if (filters.startDate) params.set('from', filters.startDate);
    if (filters.endDate) params.set('to', filters.endDate);
  }
  if (filters.searchQuery) {
    params.set('q', filters.searchQuery);
  }

  return params;
};

export const mergeInventoryTransactionFilters = (
  current: URLSearchParams,
  patch: Partial<InventoryTransactionFilters>
): URLSearchParams => {
  const merged: InventoryTransactionFilters = {
    ...parseInventoryTransactionFilters(current),
    ...patch,
  };

  if (patch.datePreset != null && patch.datePreset !== 'custom' && patch.startDate == null) {
    const range = getDateRangeForPreset(patch.datePreset);
    merged.startDate = range.startDate;
    merged.endDate = range.endDate;
  }

  return buildInventoryTransactionSearchParams(merged);
};
